// Avatar Management Utilities
// Extracted from BattleUI.js for reusability across the app

import { resolvePublicAssetUrl } from '../utils/resolvePublicAssetUrl';

/** Canonical facing in source JPG assets (all avatars face left in file). */
export const CANONICAL_AVATAR_FACING = 'left';

const DEFAULT_AVATAR_NAME = 'human_1';

/**
 * DB avatar_name values are lowercase, but a few portrait files on disk use
 * different casing (Windows dev hides this; Electron asar + Linux prod do not).
 */
const AVATAR_IMAGE_STEM_BY_NAME = Object.freeze({
  billy_1: 'Billy_1',
  david_1: 'David_1',
  isabela_1: 'Isabela_1',
});

function resolveAvatarImageStem(avatarName) {
  const normalizedName = avatarName.replace(/\s+/g, '_');
  if (/\.(png|jpg|jpeg|webp)$/i.test(normalizedName)) {
    return normalizedName;
  }
  const lookupKey = normalizedName.toLowerCase();
  return AVATAR_IMAGE_STEM_BY_NAME[lookupKey] || normalizedName;
}

/**
 * Get avatar image path from avatar name
 * @param {string} avatarName - Name of the avatar
 * @returns {string} - Image path for the avatar
 */
export function getAvatarImage(avatarName) {
  if (!avatarName) {
    return resolvePublicAssetUrl(`/images/${DEFAULT_AVATAR_NAME}.jpg`);
  }
  const stem = resolveAvatarImageStem(avatarName);
  if (/\.(png|jpg|jpeg|webp)$/i.test(stem)) {
    return resolvePublicAssetUrl(`/images/${stem}`);
  }
  return resolvePublicAssetUrl(`/images/${stem}.jpg`);
}

/**
 * Player portraits face opposite of canonical art (mirror horizontally).
 * @returns {boolean}
 */
export function shouldMirrorPlayerAvatar() {
  return true;
}

/**
 * Opponent portraits use canonical art direction (no mirror).
 * @returns {boolean}
 */
export function shouldMirrorOpponentAvatar() {
  return false;
}

/**
 * @deprecated Use shouldMirrorPlayerAvatar — kept for call-site compatibility during migration.
 */
export function shouldMirrorAvatar() {
  return shouldMirrorPlayerAvatar();
}

/**
 * Get opponent avatar image path (same as regular avatar)
 * @param {string} avatarName - Name of the avatar
 * @returns {string} - Image path for the avatar
 */
export function getOpponentAvatarImage(avatarName) {
  return getAvatarImage(avatarName);
}

/**
 * Get default avatar image path
 * @returns {string} - Default avatar image path
 */
export function getDefaultAvatarImage() {
  return resolvePublicAssetUrl(`/images/${DEFAULT_AVATAR_NAME}.jpg`);
}

/**
 * Format avatar name for file path
 * @param {string} avatarName - Name of the avatar
 * @returns {string} - Formatted name for file path
 */
export function formatAvatarName(avatarName) {
  if (!avatarName) return DEFAULT_AVATAR_NAME;
  return avatarName.toLowerCase().replace(/\s+/g, '_');
}
